(() => {
  const modal = document.querySelector('#zaaryn-quick-view');

  if (!modal) return;

  const content = modal.querySelector('[data-zaaryn-qv-content]');
  const closeButtons = modal.querySelectorAll('[data-zaaryn-qv-close]');
  const productCache = new Map();

  const loadingMarkup = `
    <div class="zaaryn-quick-view__loading">
      <span></span>
      <p>Loading...</p>
    </div>
  `;

  const errorMarkup = `
    <div class="zaaryn-quick-view__error">
      <p>Sorry, something went wrong.</p>
      <button type="button" data-zaaryn-qv-retry>Try Again</button>
    </div>
  `;

  const openModal = () => {
    if (typeof modal.showModal === 'function' && !modal.open) {
      modal.showModal();
    }
  };

  const closeModal = () => {
    if (modal.open) {
      modal.close();
    }
  };

  const getProductData = async (productUrl) => {
    if (productCache.has(productUrl)) {
      return productCache.get(productUrl);
    }

    const productPageUrl = new URL(
      productUrl,
      window.location.origin
    );

    const productPath = productPageUrl.pathname.replace(/\/$/, '');

    const productHandle = productPath
      .split('/')
      .filter(Boolean)
      .pop();

    if (!productHandle) {
      throw new Error('Product handle not found');
    }

    const response = await fetch(productPageUrl.href, {
      headers: {
        Accept: 'text/html'
      }
    });

    if (!response.ok) {
      throw new Error('Unable to load product');
    }

    const html = await response.text();

    const productDocument = new DOMParser().parseFromString(
      html,
      'text/html'
    );

    const productInfo =
      productDocument.querySelector(
        '[data-product-grid-content]'
      ) ||
      productDocument.querySelector('main');

    if (!productInfo) {
      throw new Error('Product information not found');
    }

    const root =
      window.Shopify?.routes?.root || '/';

    const productJsonUrl =
      `${root}products/${encodeURIComponent(productHandle)}.js`;

    const productResponse = await fetch(
      productJsonUrl,
      {
        headers: {
          Accept: 'application/json'
        }
      }
    );

    if (!productResponse.ok) {
      throw new Error('Unable to load product variants');
    }

    const product = await productResponse.json();

    const data = {
      document: productDocument,
      product
    };

    productCache.set(productUrl, data);

    return data;
  };

  const findProductImage = (productDocument) => {
    const image =
      productDocument.querySelector(
        '[data-product-grid-content] .product-media img'
      ) ||
      productDocument.querySelector(
        '[data-product-grid-content] img'
      ) ||
      productDocument.querySelector('main img');

    if (!image) return null;

    return {
      src: image.currentSrc || image.src,
      alt: image.alt || ''
    };
  };

  const findProductTitle = (productDocument) => {
    const title =
      productDocument.querySelector(
        '[data-product-grid-content] h1'
      ) ||
      productDocument.querySelector('main h1');

    return title ? title.textContent.trim() : '';
  };

  const findProductPrice = (productDocument) => {
    const price =
      productDocument.querySelector(
        '[data-product-grid-content] product-price'
      ) ||
      productDocument.querySelector(
        '[data-product-grid-content] .price'
      );

    return price ? price.innerHTML : '';
  };

  const findProductForm = (productDocument) => {
    return productDocument.querySelector(
      '[data-product-grid-content] product-form-component'
    );
  };

  const findOptionIndex = (product, keyword) => {
    if (!Array.isArray(product.options)) {
      return -1;
    }

    return product.options.findIndex((option) => {
      const name = String(option.name || '').toLowerCase();

      return name.includes(keyword);
    });
  };

  const getOptionValues = (product, optionIndex) => {
    return [
      ...new Set(
        product.variants
          .map((variant) => variant.options?.[optionIndex])
          .filter(Boolean)
      )
    ];
  };

  const findMatchingVariant = (
    variants,
    currentVariant,
    optionIndex,
    value
  ) => {
    const wantedOptions = [
      ...(currentVariant?.options || [])
    ];

    wantedOptions[optionIndex] = value;

    let matchingVariant = variants.find((variant) => {
      return variant.options.every(
        (option, index) =>
          option === wantedOptions[index]
      );
    });

    if (matchingVariant) {
      return matchingVariant;
    }

    matchingVariant = variants.find((variant) => {
      return (
        variant.options?.[optionIndex] === value &&
        variant.available
      );
    });

    return matchingVariant || null;
  };

  const renderOptionSelectors = ({
    product,
    selectedVariant,
    onVariantChange
  }) => {
    const container = document.createElement('div');

    container.className =
      'zaaryn-quick-view__options';

    const colorIndex =
      findOptionIndex(product, 'color');

    const sizeIndex =
      findOptionIndex(product, 'size');

    const optionIndexes = [];

    if (colorIndex !== -1) {
      optionIndexes.push(colorIndex);
    }

    if (
      sizeIndex !== -1 &&
      sizeIndex !== colorIndex
    ) {
      optionIndexes.push(sizeIndex);
    }

    optionIndexes.forEach((optionIndex) => {
      const option = product.options[optionIndex];

      const optionWrapper =
        document.createElement('div');

      optionWrapper.className =
        'zaaryn-quick-view__option';

      const optionTitle =
        document.createElement('div');

      optionTitle.className =
        'zaaryn-quick-view__option-title';

      optionTitle.textContent =
        option.name;

      const buttons =
        document.createElement('div');

      buttons.className =
        'zaaryn-quick-view__option-buttons';

      const values =
        getOptionValues(
          product,
          optionIndex
        );

      values.forEach((value) => {
        const button =
          document.createElement('button');

        button.type = 'button';

        button.className =
          'zaaryn-quick-view__option-button';

        button.textContent = value;

        button.dataset.optionIndex =
          optionIndex;

        button.dataset.optionValue =
          value;

        if (
          selectedVariant?.options?.[optionIndex] ===
          value
        ) {
          button.classList.add(
            'zaaryn-quick-view__option-button--active'
          );
        }

        button.addEventListener(
          'click',
          () => {
            onVariantChange(
              optionIndex,
              value
            );
          }
        );

        buttons.appendChild(button);
      });

      optionWrapper.appendChild(
        optionTitle
      );

      optionWrapper.appendChild(
        buttons
      );

      container.appendChild(
        optionWrapper
      );
    });

    return container;
  };

  const renderProduct = (
    productUrl,
    productData
  ) => {
    const {
      document: productDocument,
      product
    } = productData;

    const image =
      findProductImage(productDocument);

    const title =
      findProductTitle(productDocument);

    const price =
      findProductPrice(productDocument);

    const form =
      findProductForm(productDocument);

    if (!form) {
      throw new Error(
        'Product form not found'
      );
    }

    const formClone =
      form.cloneNode(true);

    const variants =
      Array.isArray(product.variants)
        ? product.variants
        : [];

    let selectedVariant =
      variants.find(
        (variant) => variant.available
      ) ||
      variants[0];

    if (!selectedVariant) {
      throw new Error(
        'No product variant found'
      );
    }

    const wrapper =
      document.createElement('div');

    wrapper.className =
      'zaaryn-quick-view__product';

    const mediaColumn =
      document.createElement('div');

    mediaColumn.className =
      'zaaryn-quick-view__media';

    if (image) {
      const imageElement =
        document.createElement('img');

      imageElement.src =
        image.src;

      imageElement.alt =
        image.alt;

      imageElement.loading =
        'eager';

      mediaColumn.appendChild(
        imageElement
      );
    }

    const infoColumn =
      document.createElement('div');

    infoColumn.className =
      'zaaryn-quick-view__info';

    const titleElement =
      document.createElement('h2');

    titleElement.id =
      'zaaryn-quick-view-title';

    titleElement.textContent =
      title ||
      product.title ||
      'Product';

    infoColumn.appendChild(
      titleElement
    );

    if (price) {
      const priceElement =
        document.createElement('div');

      priceElement.className =
        'zaaryn-quick-view__price';

      priceElement.innerHTML =
        price;

      infoColumn.appendChild(
        priceElement
      );
    }

    const variantInput =
      formClone.querySelector(
        'input[name="id"]'
      );

    const updateVariantUI = () => {
      if (
        variantInput &&
        selectedVariant
      ) {
        variantInput.value =
          selectedVariant.id;
      }

      optionsContainer
        .querySelectorAll(
          '.zaaryn-quick-view__option-button'
        )
        .forEach((button) => {
          const index =
            Number(
              button.dataset.optionIndex
            );

          const value =
            button.dataset.optionValue;

          button.classList.toggle(
            'zaaryn-quick-view__option-button--active',
            selectedVariant.options?.[index] ===
              value
          );
        });
    };

    const optionsContainer =
      renderOptionSelectors({
        product,
        selectedVariant,
        onVariantChange: (
          optionIndex,
          value
        ) => {
          const matchingVariant =
            findMatchingVariant(
              variants,
              selectedVariant,
              optionIndex,
              value
            );

          if (!matchingVariant) {
            return;
          }

          selectedVariant =
            matchingVariant;

          updateVariantUI();
        }
      });

    if (
      optionsContainer.children.length
    ) {
      infoColumn.appendChild(
        optionsContainer
      );
    }

    const formContainer =
      document.createElement('div');

    formContainer.className =
      'zaaryn-quick-view__form';

    formContainer.appendChild(
      formClone
    );

    infoColumn.appendChild(
      formContainer
    );

    const productLink =
      document.createElement('a');

    productLink.href =
      productUrl;

    productLink.className =
      'zaaryn-quick-view__view-product';

    productLink.textContent =
      'View Product';

    infoColumn.appendChild(
      productLink
    );

    wrapper.appendChild(
      mediaColumn
    );

    wrapper.appendChild(
      infoColumn
    );

    content.replaceChildren(
      wrapper
    );

    updateVariantUI();
  };

  const loadProduct = async (
    productUrl
  ) => {
    content.innerHTML =
      loadingMarkup;

    openModal();

    try {
      const productData =
        await getProductData(
          productUrl
        );

      renderProduct(
        productUrl,
        productData
      );
    } catch (error) {
      console.error(
        'ZAARYN Quick View:',
        error
      );

      content.innerHTML =
        errorMarkup;

      const retryButton =
        content.querySelector(
          '[data-zaaryn-qv-retry]'
        );

      retryButton?.addEventListener(
        'click',
        () => {
          loadProduct(productUrl);
        }
      );
    }
  };

  document.addEventListener(
    'click',
    (event) => {
      const button =
        event.target.closest(
          '[data-zaaryn-quick-view]'
        );

      if (!button) return;

      event.preventDefault();
      event.stopPropagation();

      const productUrl =
        button.dataset.productUrl;

      if (!productUrl) return;

      loadProduct(productUrl);
    }
  );

  closeButtons.forEach(
    (button) => {
      button.addEventListener(
        'click',
        closeModal
      );
    }
  );

  modal.addEventListener(
    'cancel',
    closeModal
  );
})();